/**
 * Unified Storage Utility
 * 
 * Uses Capacitor Preferences on native platforms (Android/iOS) for persistent storage
 * Falls back to localStorage on web browsers
 * 
 * This ensures authentication tokens and user data persist across app restarts
 * on mobile devices, where localStorage in WebView can be cleared.
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

class StorageService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    console.log(`🔧 StorageService initialized. Is Native Platform: ${this.isNative}`);
    
    // Test Preferences on native to ensure it's working
    if (this.isNative) {
      this.testPreferences();
    }
  }
  
  async testPreferences() {
    try {
      const testKey = '__storage_test__';
      const testValue = 'test_' + Date.now();
      await Preferences.set({ key: testKey, value: testValue });
      const result = await Preferences.get({ key: testKey });
      if (result.value === testValue) {
        console.log('✅ Capacitor Preferences is working correctly');
        await Preferences.remove({ key: testKey });
      } else {
        console.error('❌ Capacitor Preferences test failed: value mismatch');
      }
    } catch (error) {
      console.error('❌ Capacitor Preferences test failed:', error);
    }
  }

  /**
   * Set an item in storage
   * @param {string} key - Storage key
   * @param {any} value - Value to store (will be JSON stringified if object)
   */
  async setItem(key, value) {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      
      if (this.isNative) {
        // On native platforms, save to BOTH Capacitor Preferences (persistent) 
        // AND localStorage (for immediate sync access)
        // This ensures tokens are available both immediately and after app restart
        
        // CRITICAL: Save to Capacitor Preferences FIRST (this is what persists)
        try {
          await Preferences.set({
            key,
            value: stringValue
          });
          console.log(`✅ Saved ${key} to Capacitor Preferences (persistent storage)`, {
            key,
            valueLength: stringValue.length,
            preview: stringValue.substring(0, 30) + '...'
          });
          
          // Verify it was saved by reading it back
          const verify = await Preferences.get({ key });
          if (verify.value === stringValue) {
            console.log(`✅ Verified ${key} saved correctly to Preferences`);
          } else {
            console.error(`❌ Verification failed: ${key} value mismatch!`);
          }
        } catch (prefError) {
          console.error(`❌ CRITICAL: Error saving ${key} to Preferences:`, prefError);
          throw prefError; // Don't continue if Preferences save fails
        }
        
        // Also save to localStorage for immediate sync access (secondary)
        try {
          localStorage.setItem(key, stringValue);
          console.log(`✅ Also saved ${key} to localStorage (for fast access)`);
        } catch (localError) {
          console.warn(`⚠️ Warning: Could not save ${key} to localStorage:`, localError);
          // Don't throw - Preferences save succeeded, which is what matters
        }
      } else {
        // Use localStorage on web
        localStorage.setItem(key, stringValue);
      }
    } catch (error) {
      console.error(`Error setting storage item ${key}:`, error);
      // Final fallback
      try {
        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
        localStorage.setItem(key, stringValue);
      } catch (fallbackError) {
        console.error(`Fallback localStorage also failed for ${key}:`, fallbackError);
      }
    }
  }

  /**
   * Get an item from storage
   * @param {string} key - Storage key
   * @returns {Promise<string|null>} - Stored value or null
   */
  async getItem(key) {
    try {
      if (this.isNative) {
        // Use Capacitor Preferences on native platforms
        const result = await Preferences.get({ key });
        const value = result.value;
        console.log(`📖 Read ${key} from Preferences:`, { 
          found: !!value, 
          length: value?.length || 0,
          preview: value ? value.substring(0, 20) + '...' : null
        });
        return value;
      } else {
        // Use localStorage on web
        return localStorage.getItem(key);
      }
    } catch (error) {
      console.error(`❌ Error getting storage item ${key} from Preferences:`, error);
      // Fallback to localStorage if Preferences fails
      if (this.isNative) {
        try {
          const fallbackValue = localStorage.getItem(key);
          console.log(`⚠️ Fallback: Read ${key} from localStorage:`, { found: !!fallbackValue });
          return fallbackValue;
        } catch (fallbackError) {
          console.error(`❌ Fallback localStorage also failed for ${key}:`, fallbackError);
          return null;
        }
      }
      return null;
    }
  }

  /**
   * Get an item from storage synchronously (for initial state)
   * This will try localStorage first for immediate access, then Preferences
   * @param {string} key - Storage key
   * @returns {string|null} - Stored value or null
   */
  getItemSync(key) {
    try {
      // On native, try localStorage first for synchronous access during initialization
      // This works because we now save to both localStorage AND Preferences
      // If not found in localStorage, async read from Preferences will handle it
      const value = localStorage.getItem(key);
      if (value) {
        console.log(`✅ Found ${key} in localStorage (sync)`);
      }
      return value;
    } catch (error) {
      console.error(`Error getting storage item synchronously ${key}:`, error);
      return null;
    }
  }

  /**
   * Remove an item from storage
   * @param {string} key - Storage key
   */
  async removeItem(key) {
    try {
      if (this.isNative) {
        // Use Capacitor Preferences on native platforms
        await Preferences.remove({ key });
      } else {
        // Use localStorage on web
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.error(`Error removing storage item ${key}:`, error);
      // Fallback to localStorage if Preferences fails
      if (this.isNative) {
        try {
          localStorage.removeItem(key);
        } catch (fallbackError) {
          console.error(`Fallback localStorage also failed for ${key}:`, fallbackError);
        }
      }
    }
  }

  /**
   * Clear all storage items
   * WARNING: This will clear ALL preferences, use with caution
   */
  async clear() {
    try {
      if (this.isNative) {
        // Use Capacitor Preferences on native platforms
        await Preferences.clear();
      } else {
        // Use localStorage on web
        localStorage.clear();
      }
    } catch (error) {
      console.error('Error clearing storage:', error);
      // Fallback to localStorage if Preferences fails
      if (this.isNative) {
        try {
          localStorage.clear();
        } catch (fallbackError) {
          console.error('Fallback localStorage clear also failed:', fallbackError);
        }
      }
    }
  }

  /**
   * Get all keys from storage
   * @returns {Promise<string[]>} - Array of storage keys
   */
  async keys() {
    try {
      if (this.isNative) {
        // Use Capacitor Preferences on native platforms
        const result = await Preferences.keys();
        return result.keys;
      } else {
        // Use localStorage on web
        return Object.keys(localStorage);
      }
    } catch (error) {
      console.error('Error getting storage keys:', error);
      // Fallback to localStorage if Preferences fails
      if (this.isNative) {
        try {
          return Object.keys(localStorage);
        } catch (fallbackError) {
          console.error('Fallback localStorage keys also failed:', fallbackError);
          return [];
        }
      }
      return [];
    }
  }
}

// Create singleton instance
const storage = new StorageService();

// Export both default and named exports
export default storage;
export { storage };

